import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MonitorCheck, Globe } from 'lucide-react'
import type { AuthState } from '@/hooks/useAuth'
import { useProxyConfig } from '@/hooks/useProxyConfig'
import { useProviderTapTrigger } from '@/hooks/useProviderTrigger'
import { MemberSearch } from '@/components/shared/MemberSearch'
import { BackupStatusBadge } from './BackupStatusBadge'
import type { User } from '@/api/users'

interface TopbarProps {
  auth: AuthState
  can: (permission: string) => boolean
}

type ConnectionMode = 'local' | 'remote'

// Reflects the install's actual networkTopology instead of assuming "local" —
// see openspec/changes/mqtt-remote-mode-relay.
function useConnectionMode(isElectron: boolean, proxyUrl: string | null, proxyToken: string | null): ConnectionMode {
  const [mode, setMode] = useState<ConnectionMode>('local')

  useEffect(() => {
    if (!isElectron || !proxyUrl) return
    const headers: Record<string, string> = proxyToken ? { Authorization: `Bearer ${proxyToken}` } : {}
    fetch(`${proxyUrl}/local/network-topology`, { headers })
      .then(r => r.json())
      .then((data: { topology?: ConnectionMode }) => setMode(data.topology ?? 'local'))
      .catch(() => {})
  }, [isElectron, proxyUrl, proxyToken])

  return mode
}

export function Topbar({ auth: _auth, can: _can }: TopbarProps) {
  const navigate = useNavigate()
  const { isElectron, appVersion, proxyUrl, proxyToken } = useProxyConfig()
  const connectionMode = useConnectionMode(isElectron, proxyUrl, proxyToken)
  const handleBadgeTap = useProviderTapTrigger()

  function handleMemberSelect(user: User) {
    navigate(`/members/${user.id}`)
  }

  return (
    <header
      // relative + z-30: backdrop-blur below creates its own stacking context,
      // which traps the search dropdown's z-index inside it — without this,
      // the whole header (dropdown included) painted behind the sidebar/main
      // content, since that sibling comes later in the DOM with no z-index
      // of its own to lose the comparison against.
      className="relative z-30 h-12 flex items-center px-4 gap-3 shrink-0 border-b border-white/8 backdrop-blur-sm"
      style={{ background: 'linear-gradient(90deg, #0a1624 0%, #0c1d14 50%, #0a1624 100%)' }}
    >
      <div className="flex-1 relative max-w-lg">
        <MemberSearch
          onSelect={handleMemberSelect}
          placeholder="Buscar miembro por nombre o cédula..."
          inputClassName="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-2 text-sm text-white/80 placeholder:text-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 focus:border-white/20 transition-all disabled:opacity-50"
        />
        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[9px] text-white/20 bg-white/5 border border-white/8 px-1.5 py-0.5 rounded font-mono pointer-events-none">
          Ctrl+K
        </span>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {isElectron && <BackupStatusBadge />}
        {isElectron ? (
          <span
            onClick={handleBadgeTap}
            title="Veridia"
            className="flex items-center gap-1.5 text-[11px] font-medium text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-1 rounded-full cursor-pointer select-none transition-transform active:scale-95"
          >
            <MonitorCheck className="w-3 h-3 shrink-0" />
            {connectionMode === 'remote' ? 'Remoto' : 'Local'}{appVersion ? ` v${appVersion}` : ''}
          </span>
        ) : (
          <span
            onClick={handleBadgeTap}
            title="Veridia"
            className="flex items-center gap-1.5 text-[11px] font-medium text-white/35 bg-white/5 border border-white/10 px-2.5 py-1 rounded-full cursor-pointer select-none transition-transform active:scale-95"
          >
            <Globe className="w-3 h-3 shrink-0" />
            Web
          </span>
        )}
      </div>
    </header>
  )
}
